// Large data tables panel.
//
// Sits inside the export builder so the full row-level datasets can be
// downloaded on their own, without composing a document. Each table shows its
// live row count and a download button per format (CSV / Excel / JSON / PDF).
// The composed document caps tables for readability; this panel is where the
// complete data lives, so the two never fight over "is this everything?".
//
// Rows are pulled lazily on download (getRows) so the panel itself stays
// cheap even when a table holds a few thousand rows.
import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import MainCard from '../../../components/cards/MainCard';
import { LARGE_TABLES, TABLE_FORMATS, downloadBlob } from '../exportData';
import useDashboardData from '../hooks/useDashboardData';
import { useGetDwmBreachedUsersQuery } from '../../../store/services/dwm-service';
import { useAppendExportHistoryMutation } from '../../../store/services/reportLayout.service';
import { generateTable } from '../exportGenerators';
import useFeature from '../useFeature';

export default function LargeTablesPanel({ onToast, scope }) {
    const dwmOn = useFeature('darkWebMonitoring');
    const { tenancyId, tenancyName } = useSelector((state) => state.tenancy);
    const brand = (tenancyName || '').trim() || 'Boxphish';
    const [busy, setBusy] = useState(null);
    const [appendHistory] = useAppendExportHistoryMutation();
    const tables = LARGE_TABLES.filter((t) => !t.feature || (t.feature === 'darkWebMonitoring' ? dwmOn : true));

    // Server-fed tables: the All users export pulls the same roster aggregation
    // as the main Users table (full field set, includes disabled), and the DWM
    // table pulls every breached email from the dwm-api, joined to the roster
    // for name/department. Both fall back to the client dataset while loading.
    // The All users export reads the roster rollup directly (not the client-
    // pooled ./data), so a Tenancy filter is forwarded to pool the selected
    // child tenancies' users into the export.
    const tenancyScope = Array.isArray(scope?.tenancy) && scope.tenancy.length ? { tenancy: scope.tenancy } : undefined;
    const rosterQ = useDashboardData({
        metric: 'users', aggregation: 'user_roster', limit: 20000,
        engagement: 'all', includeDisabled: true, period: '12m',
        sort: { field: 'riskScore', direction: 'desc' },
        scope: tenancyScope,
    });
    const dwmQ = useGetDwmBreachedUsersQuery({ tenancyId, page: 1, limit: 1000 }, { skip: !dwmOn || !tenancyId });
    const liveRows = useMemo(() => {
        const roster = Array.isArray(rosterQ.data?.roster) ? rosterQ.data.roster : null;
        let dwm = null;
        if (Array.isArray(dwmQ.data?.users)) {
            const byEmail = new Map((roster || []).map((r) => [String(r.email || '').toLowerCase(), r]));
            dwm = [...dwmQ.data.users]
                .sort((a, b) => (b.breaches ?? 0) - (a.breaches ?? 0))
                .map((u) => {
                    const match = byEmail.get(String(u.email || '').toLowerCase());
                    return {
                        email: u.email,
                        name: match ? `${match.firstName || ''} ${match.lastName || ''}`.trim() : '',
                        dept: match?.dept || '-',
                        mentions: u.breaches ?? 0,
                        withCreds: u.passwordAvailable ?? 0,
                        sources: '-',
                        latest: u.latestBreachAt
                            ? new Date(`${u.latestBreachAt}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
                            : '-',
                    };
                });
        }
        return { users: roster, 'dwm-by-email': dwm };
    }, [rosterQ.data, dwmQ.data]);

    const handleDownload = (table, format) => {
        setBusy(`${table.id}-${format}`);
        // Defer so the button shows its pressed state before a large PDF blocks
        // the main thread; keeps the click feeling responsive.
        setTimeout(() => {
            try {
                const rows = table.getRows(liveRows[table.id]);
                const { content, mime, filename } = generateTable(format, table.label, table.columns, rows, { brand });
                downloadBlob(content, mime, filename);
                appendHistory({ tenancyId, entry: { kind: 'table', via: 'download', title: table.label, format, tableId: table.id, rows: rows.length } });
                onToast && onToast(`Downloaded ${filename} (${rows.length.toLocaleString()} rows)`);
            } finally {
                setBusy(null);
            }
        }, 10);
    };

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                <TableChartOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                        Large data tables
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        Download a full dataset on its own, in any format. {tables.length} tables available.
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ p: 1.5 }}>
                {tables.map((t) => {
                    const count = t.getRows(liveRows[t.id]).length;
                    return (
                        <Box
                            key={t.id}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                                px: 1.5, py: 1.25, borderRadius: 1.5,
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 180 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.primary' }}>{t.label}</Typography>
                                    <Typography sx={{ fontSize: 9.5, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.75 }}>{t.source}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{t.description}</Typography>
                                <Typography sx={{ fontSize: 9.5, color: 'text.disabled' }}>{count.toLocaleString()} rows · {t.columns.length} columns</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {TABLE_FORMATS.map((f) => {
                                    const isBusy = busy === `${t.id}-${f}`;
                                    return (
                                        <Tooltip key={f} title={`Download ${t.label} as ${f}`}>
                                            <span>
                                                <Button
                                                    size="small" variant="outlined"
                                                    onClick={() => handleDownload(t, f)}
                                                    disabled={!!busy}
                                                    startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 13 }} />}
                                                    sx={{
                                                        minWidth: 0, fontSize: 10.5, fontWeight: 600, px: 1, py: '2px', borderRadius: '4px',
                                                        textTransform: 'none', borderColor: 'divider', color: 'text.secondary',
                                                        '& .MuiButton-startIcon': { mr: 0.5 },
                                                        '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' },
                                                    }}
                                                >
                                                    {isBusy ? '...' : f}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </MainCard>
    );
}
